import { tosApi } from './tosApi';

export type HealthStatus =
  | 'operational'
  | 'degraded'
  | 'unavailable'
  | 'unknown';

export type HealthHistoryItem = {
  at: string;
  status: HealthStatus;
  reason: string;
  latency_ms: number | null;
};

export type HealthDependency = {
  key: string;
  label: string;
  owner: string;
  direction: string;
  mode: string;
  interval_seconds: number;
  notes: string;
  status: HealthStatus;
  reason: string;
  // A medição é velha demais para descrever o presente (mais de três intervalos
  // sem resultado novo). O backend já rebaixa status/reason; este campo existe
  // para a tela esmaecer a faixa de histórico em vez de mostrar tracinhos
  // verdes sob um card cinza.
  stale?: boolean;
  latency_ms: number | null;
  checked_at: string | null;
  next_check_at: string | null;
  detail: Record<string, unknown>;
  history: HealthHistoryItem[];
};

export type HealthGroup = {
  key: string;
  label: string;
  dependencies: HealthDependency[];
};

export type HealthDependenciesResponse = {
  generated_at: string;
  environment: string;
  overall: HealthStatus;
  groups: HealthGroup[];
};

export type IntegrationLogItem = {
  id: number;
  created_at: string | null;
  direction: string;
  action: string;
  // O que a chamada faz, em português, vindo do catálogo do backend. Nulo
  // quando a ação ainda não foi mapeada — aí a tela cai no `action`.
  action_label: string | null;
  method: string | null;
  endpoint: string | null;
  status: string | null;
  is_error: boolean;
  description: string | null;
  application: string | null;
};

export type IntegrationLogsResponse = {
  results: IntegrationLogItem[];
  pagination: {
    page: number;
    page_size: number;
    total_count: number;
    total_pages: number;
    has_next: boolean;
    has_previous: boolean;
  };
};

export const integrationHealthApi = tosApi.injectEndpoints({
  overrideExisting: true,
  endpoints: (build) => ({
    getIntegrationHealth: build.query<HealthDependenciesResponse, void>({
      query: () => ({
        url: '/integration/health/dependencies',
        method: 'GET',
      }),
    }),
    // Disparo manual: verifica um serviço na hora, sem esperar o beat. Devolve
    // o card já atualizado.
    checkDependency: build.mutation<HealthDependency, string>({
      query: (serviceKey) => ({
        url: `/integration/health/dependencies/${serviceKey}/check`,
        method: 'POST',
      }),
    }),
    // Verifica todos os serviços agora (dispara a checagem no worker; a tela
    // relê em seguida).
    checkAllDependencies: build.mutation<{ status: string }, void>({
      query: () => ({
        url: '/integration/health/check-all',
        method: 'POST',
      }),
    }),
    getDependencyLogs: build.query<
      IntegrationLogsResponse,
      { serviceKey: string; page?: number; pageSize?: number }
    >({
      query: ({ serviceKey, page = 1, pageSize = 20 }) => ({
        url: `/integration/health/dependencies/${serviceKey}/logs?page=${page}&page_size=${pageSize}`,
        method: 'GET',
      }),
    }),
  }),
});

export const {
  useGetIntegrationHealthQuery,
  useCheckDependencyMutation,
  useCheckAllDependenciesMutation,
  useLazyGetDependencyLogsQuery,
} = integrationHealthApi;
